import { groupBy, orderBy } from "lodash";
import { createSelector } from "reselect";

import { getCurrentDeploymentSaleIds } from "./currentSale";
import {
  selectBuyerIdBySaleLotIdLookup,
  selectVendorIdBySaleLotIdLookup,
} from "./indexes";
import { getBusinesses, getEContracts } from "./root";
import { selectSaleLotIdsByDeploymentSaleIdLookup } from "./saleLotsCalculated";

export const getEContractById: (
  eContractId: string,
) => (state: State) => EContract | null = eContractId => state =>
  getEContracts(state)[eContractId] || null;

export const selectEContractsInCurrentSaleList: (state: State) => EContract[] =
  createSelector(
    [getCurrentDeploymentSaleIds, getEContracts],
    (deploymentSaleIds: number[], eContracts: Record<string, EContract>) => {
      return Object.values(eContracts).filter(ec =>
        deploymentSaleIds.includes(ec.deploymentSaleId),
      );
    },
  );

export const selectEContracts: (state: State) => Record<string, EContract[]> =
  createSelector(
    [selectEContractsInCurrentSaleList],
    (eContractsInSale: EContract[]) => {
      return groupBy(orderBy(eContractsInSale, ["version"], ["desc"]), "code");
    },
  );

interface EContractLotCoverage {
  allSaleLotIds: string[];
  activeEContracts: EContract[];
  activeEContractSaleLotIds: Set<string>;
  vendorIdBySaleLotId: Record<string, string>;
  buyerIdBySaleLotId: Record<string, string>;
  // Keyed by `${vendorBusinessId}_${buyerBusinessId}`. Only lots with both IDs
  // are counted - unsold/no-buyer lots can't form a pair and are excluded.
  uncoveredCountByPair: Map<string, number>;
}

const selectEContractLotCoverageBase: (state: State) => EContractLotCoverage =
  createSelector(
    [
      getCurrentDeploymentSaleIds as (state: State) => number[],
      selectSaleLotIdsByDeploymentSaleIdLookup as (
        state: State,
      ) => Record<number, string[]>,
      selectEContractsInCurrentSaleList,
      selectVendorIdBySaleLotIdLookup as (
        state: State,
      ) => Record<string, string>,
      selectBuyerIdBySaleLotIdLookup as (
        state: State,
      ) => Record<string, string>,
    ],
    (
      deploymentSaleIds: number[],
      saleLotIdsByDeploymentSaleId: Record<number, string[]>,
      eContractsInSale: EContract[],
      vendorIdBySaleLotId: Record<string, string>,
      buyerIdBySaleLotId: Record<string, string>,
    ) => {
      const allSaleLotIds = deploymentSaleIds.reduce(
        (acc, deploymentSaleId) => {
          return acc.concat(
            saleLotIdsByDeploymentSaleId[deploymentSaleId] || [],
          );
        },
        [] as string[],
      );

      const activeEContracts = eContractsInSale.filter(
        ec => ec.id && !ec.voidedOn,
      );

      const activeEContractSaleLotIds = new Set<string>();
      activeEContracts.forEach(ec => {
        ec.saleLotIds.forEach(id => activeEContractSaleLotIds.add(id));
      });

      const uncoveredCountByPair = new Map<string, number>();
      allSaleLotIds.forEach(saleLotId => {
        if (activeEContractSaleLotIds.has(saleLotId)) return;
        const vendorId = vendorIdBySaleLotId[saleLotId];
        const buyerId = buyerIdBySaleLotId[saleLotId];
        if (vendorId && buyerId) {
          const key = `${vendorId}_${buyerId}`;
          uncoveredCountByPair.set(
            key,
            (uncoveredCountByPair.get(key) || 0) + 1,
          );
        }
      });

      return {
        allSaleLotIds,
        activeEContracts,
        activeEContractSaleLotIds,
        vendorIdBySaleLotId,
        buyerIdBySaleLotId,
        uncoveredCountByPair,
      };
    },
  );

// For each active eContract code, the number of sale lots that share the same
// vendor/buyer pair but are not yet included in any active eContract.
export const selectExtraLotCountByActiveEContractCode: (
  state: State,
) => Record<string, number> = createSelector(
  [selectEContractLotCoverageBase],
  ({ activeEContracts, uncoveredCountByPair }) => {
    const latestActiveByCode = groupBy(
      orderBy(activeEContracts, ["version"], ["desc"]),
      "code",
    );

    return Object.entries(latestActiveByCode).reduce(
      (acc, [code, contracts]) => {
        const latest = contracts[0];
        if (latest.vendorBusinessId && latest.buyerBusinessId) {
          const key = `${latest.vendorBusinessId}_${latest.buyerBusinessId}`;
          acc[code] = uncoveredCountByPair.get(key) || 0;
        } else {
          acc[code] = 0;
        }
        return acc;
      },
      {} as Record<string, number>,
    );
  },
);

export const getBuyerByEContractId: (
  eContractId: string | null | undefined,
) => (state: State) => Business | null = eContractId => state => {
  if (!eContractId) {
    return null;
  }
  const eContract = getEContractById(eContractId)(state);
  if (!eContract) {
    return null;
  }
  return getBusinesses(state)[eContract.buyerBusinessId] || null;
};

export const getVendorByEContractId: (
  eContractId: string | null | undefined,
) => (state: State) => Business | null = eContractId => state => {
  if (!eContractId) {
    return null;
  }
  const eContract = getEContractById(eContractId)(state);
  if (!eContract) {
    return null;
  }
  return getBusinesses(state)[eContract.vendorBusinessId] || null;
};
